import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
// Constants - Functions
import { formatTimeDifference } from "../../../../../config/functions";
import { levelsByProtocol, isOnlyRGSICUVideoProtocol } from "../../../functions";
// Components
import {
  SessionDiv,
  SessionInfoDiv,
  ProtocolCardDiv,
  ProtocolImgDiv,
  ProtocolImg,
  ProtocolName,
  TimeDataDiv,
  TimeDataText,
  SessionDetailsDiv,
  SessionDetailsButton,
  ArrowImg,
  HitsAndErrorsDiv,
  HitsAndErrorsRow,
  HitsAndErrorsBox,
  HitsAndErrorsText,
} from "./styles";
// Assets
import IcoArrowUp from "../../../../../assets/icons/ico_arrow_up_blue.svg";
import IcoArrowDown from "../../../../../assets/icons/ico_arrow_down_blue.svg";

const ProtocolSession = ({ session, protocolImg, protocolName }) => {
  const [isShowingSessionDetails, setIsShowingSessionDetails] = useState(false);
  const [levels] = useState(new Array(levelsByProtocol).fill(0).map((_, i) => i));
  const [t] = useTranslation("global");

  const renderProtocolCard = () => {
    return (
      <ProtocolCardDiv>
        <ProtocolImgDiv>
          <ProtocolImg src={protocolImg} alt="protocol-img" />
        </ProtocolImgDiv>
        <ProtocolName>{protocolName}</ProtocolName>
      </ProtocolCardDiv>
    );
  };

  const renderStartTime = () => {
    const parsedTime = moment(session.STARTING_DATE).format("HH:mm");
    return (
      <TimeDataDiv>
        <TimeDataText>{parsedTime}</TimeDataText>
      </TimeDataDiv>
    );
  };

  const renderEndTime = () => {
    const parsedTime = moment(session.ENDING_DATE).format("HH:mm");
    return (
      <TimeDataDiv>
        <TimeDataText>{parsedTime}</TimeDataText>
      </TimeDataDiv>
    );
  };

  const renderDifferenceTime = () => {
    const parsedTime = formatTimeDifference(session.STARTING_DATE, session.ENDING_DATE);
    return (
      <TimeDataDiv>
        <TimeDataText>{parsedTime}</TimeDataText>
      </TimeDataDiv>
    );
  };

  const renderDetailsButton = () => {
    return (
      <SessionDetailsDiv>
        <SessionDetailsButton onClick={() => setIsShowingSessionDetails(!isShowingSessionDetails)}>
          <ArrowImg src={isShowingSessionDetails ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />
        </SessionDetailsButton>
      </SessionDetailsDiv>
    );
  };

  const renderHitsAndErrorsSection = () => {
    let totalHits = "-";
    let totalErrors = "-";

    const renderHeader = () => {
      return (
        <HitsAndErrorsRow>
          <HitsAndErrorsBox topShadow background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("level")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox topShadow background="#F2F2F2F2" borderLeft="2px solid #fff">
            <HitsAndErrorsText strong>{t("hits")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox topShadow background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("errors")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox topShadow background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("rgsicu_successRate")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
        </HitsAndErrorsRow>
      );
    };

    const renderLevels = () => {
      return levels.map((level) => {
        const hits = session.hasOwnProperty(`totalHits_level${level}`)
          ? Number(session[`totalHits_level${level}`])
          : session.hasOwnProperty(`totalErrors_level${level}`)
          ? 0
          : "-";

        const errors = session.hasOwnProperty(`totalErrors_level${level}`)
          ? Number(session[`totalErrors_level${level}`])
          : session.hasOwnProperty(`totalHits_level${level}`)
          ? 0
          : "-";

        const successRate = isNaN(hits) || isNaN(errors) ? "-" : `${parseInt((hits / (hits + errors)) * 100)}%`;

        if (!isNaN(hits) && !isNaN(errors)) {
          totalHits = isNaN(totalHits) ? hits : hits + totalHits;
          totalErrors = isNaN(totalErrors) ? errors : errors + totalErrors;
        }

        return (
          <HitsAndErrorsRow key={level}>
            <HitsAndErrorsBox background="#F2F2F2F2">
              <HitsAndErrorsText strong>{level + 1}</HitsAndErrorsText>
            </HitsAndErrorsBox>
            <HitsAndErrorsBox background="#FFF" borderBottom="2px solid #F2F2F2F2">
              <HitsAndErrorsText>{hits}</HitsAndErrorsText>
            </HitsAndErrorsBox>
            <HitsAndErrorsBox background="#FFF" borderBottom="2px solid #F2F2F2F2">
              <HitsAndErrorsText>{errors}</HitsAndErrorsText>
            </HitsAndErrorsBox>
            <HitsAndErrorsBox background="#FFF" borderBottom="2px solid #F2F2F2F2">
              <HitsAndErrorsText>{successRate}</HitsAndErrorsText>
            </HitsAndErrorsBox>
          </HitsAndErrorsRow>
        );
      });
    };

    const renderTotalLevels = () => {
      const totalSuccessRate = isNaN(totalHits) || isNaN(totalErrors) ? "-" : `${parseInt((totalHits / (totalHits + totalErrors)) * 100)}%`;
      return (
        <HitsAndErrorsRow>
          <HitsAndErrorsBox background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("total")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox background="#FFF">
            <HitsAndErrorsText>{totalHits}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox background="#FFF">
            <HitsAndErrorsText>{totalErrors}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox background="#FFF">
            <HitsAndErrorsText>{totalSuccessRate}</HitsAndErrorsText>
          </HitsAndErrorsBox>
        </HitsAndErrorsRow>
      );
    };

    return (
      <HitsAndErrorsDiv>
        {renderHeader()}
        {renderLevels()}
        {renderTotalLevels()}
      </HitsAndErrorsDiv>
    );
  };

  return (
    <SessionDiv>
      <SessionInfoDiv>
        {renderProtocolCard()}
        {renderStartTime()}
        {renderEndTime()}
        {renderDifferenceTime()}
        {!isOnlyRGSICUVideoProtocol(session.PROTOCOL_ID) && renderDetailsButton()}
      </SessionInfoDiv>
      {isShowingSessionDetails && renderHitsAndErrorsSection()}
    </SessionDiv>
  );
};

export default ProtocolSession;
